'use client';

import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { CssBaseline } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useEffect, useMemo } from 'react';
import { createTheme } from './theme';

type ThemeProviderProps = {
  children: React.ReactNode;
  darkMode?: boolean;
  baseUrl?: string;
  cssVariables?: boolean;
};

const ThemeProvider = ({
  children,
  darkMode,
  baseUrl,
  cssVariables = false,
}: ThemeProviderProps) => {
  const theme = useMemo(
    () => createTheme(darkMode, baseUrl, cssVariables),
    [baseUrl, darkMode, cssVariables],
  );

  useEffect(() => {
    if (cssVariables) return;
    document.documentElement.style.setProperty(
      'color-scheme',
      darkMode ? 'dark' : 'light',
    );
  }, [darkMode, cssVariables]);

  // Browsers always match `:focus-visible` on text fields, even on a mouse
  // click, so pure CSS can't restrict the dashed focus outline on inputs to
  // keyboard navigation. This tracks the last input modality and toggles a
  // class on `<html>` so `theme.ts` can scope the input outline to it.
  useEffect(() => {
    const root = document.documentElement;

    const enableKeyboardNav = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        root.classList.add('keyboard-nav');
      }
    };
    const disableKeyboardNav = () => {
      root.classList.remove('keyboard-nav');
    };

    window.addEventListener('keydown', enableKeyboardNav);
    window.addEventListener('mousedown', disableKeyboardNav);
    window.addEventListener('pointerdown', disableKeyboardNav);

    return () => {
      window.removeEventListener('keydown', enableKeyboardNav);
      window.removeEventListener('mousedown', disableKeyboardNav);
      window.removeEventListener('pointerdown', disableKeyboardNav);
    };
  }, []);

  return (
    <EmotionThemeProvider theme={theme}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </EmotionThemeProvider>
  );
};

export default ThemeProvider;
